export const MIN_DESKTOP_WIDTH_PX = 1100
export const DESKTOP_EXPERIENCE_QUERY = `(min-width: ${MIN_DESKTOP_WIDTH_PX}px) and (hover: hover) and (pointer: fine)`

export interface DesktopCapabilities {
  viewportWidth: number
  hover: boolean
  finePointer: boolean
}

export const supportsDesktopExperience = ({ viewportWidth, hover, finePointer }: DesktopCapabilities) => (
  viewportWidth >= MIN_DESKTOP_WIDTH_PX && hover && finePointer
)

export interface PwaState {
  desktopSupported: boolean
  installed: boolean
  installAvailable: boolean
  updateAvailable: boolean
  offlineReady: boolean
}

export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable'

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

type PwaListener = () => void

let state: PwaState = {
  desktopSupported: false,
  installed: false,
  installAvailable: false,
  updateAvailable: false,
  offlineReady: false,
}
let initialized = false
let deferredInstall: InstallPromptEvent | null = null
let registration: ServiceWorkerRegistration | null = null
let reloadForUpdate = false
const listeners = new Set<PwaListener>()

const updateState = (patch: Partial<PwaState>) => {
  const next = { ...state, ...patch }
  if (Object.keys(next).every((key) => next[key as keyof PwaState] === state[key as keyof PwaState])) return
  state = next
  listeners.forEach((listener) => listener())
}

export const getPwaState = () => state

export const subscribePwa = (listener: PwaListener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const isStandalone = () => {
  const iosStandalone = (navigator as NavigatorWithStandalone).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

const addDesktopManifest = () => {
  if (!document.querySelector('#deckelier-manifest')) {
    const manifest = document.createElement('link')
    manifest.id = 'deckelier-manifest'
    manifest.rel = 'manifest'
    manifest.href = '/manifest.webmanifest'
    document.head.append(manifest)
  }
  if (!document.querySelector('#deckelier-touch-icon')) {
    const icon = document.createElement('link')
    icon.id = 'deckelier-touch-icon'
    icon.rel = 'apple-touch-icon'
    icon.href = '/icons/apple-touch-icon.png'
    document.head.append(icon)
  }
}

const removeDesktopManifest = () => {
  document.querySelector('#deckelier-manifest')?.remove()
  document.querySelector('#deckelier-touch-icon')?.remove()
  deferredInstall = null
  updateState({ installAvailable: false })
}

const watchRegistration = (nextRegistration: ServiceWorkerRegistration) => {
  registration = nextRegistration
  if (nextRegistration.waiting && navigator.serviceWorker.controller) updateState({ updateAvailable: true })
  nextRegistration.addEventListener('updatefound', () => {
    const worker = nextRegistration.installing
    if (!worker) return
    worker.addEventListener('statechange', () => {
      if (worker.state !== 'installed') return
      if (navigator.serviceWorker.controller) updateState({ updateAvailable: true })
      else updateState({ offlineReady: true })
    })
  })
}

const registerOfflineShell = async () => {
  if (!('serviceWorker' in navigator) || registration) return
  try {
    const nextRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    watchRegistration(nextRegistration)
    await navigator.serviceWorker.ready
    updateState({ offlineReady: true })
    if (navigator.onLine) void nextRegistration.update()
  } catch {
    // The browser version remains usable online when service workers are unavailable.
  }
}

export const initializeDesktopPwa = () => {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  const desktop = window.matchMedia(DESKTOP_EXPERIENCE_QUERY)
  const standalone = window.matchMedia('(display-mode: standalone)')

  const syncDesktopSupport = () => {
    updateState({ desktopSupported: desktop.matches, installed: isStandalone() })
    if (desktop.matches) {
      addDesktopManifest()
      void registerOfflineShell()
    } else {
      removeDesktopManifest()
    }
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    if (!state.desktopSupported || state.installed) return
    event.preventDefault()
    deferredInstall = event as InstallPromptEvent
    updateState({ installAvailable: true })
  })
  window.addEventListener('appinstalled', () => {
    deferredInstall = null
    updateState({ installed: true, installAvailable: false })
  })
  window.addEventListener('online', () => {
    if (registration) void registration.update()
  })
  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (reloadForUpdate) window.location.reload()
  })
  desktop.addEventListener('change', syncDesktopSupport)
  standalone.addEventListener('change', () => updateState({ installed: isStandalone() }))
  syncDesktopSupport()
}

export const promptDesktopPwaInstall = async (): Promise<PwaInstallOutcome> => {
  if (!state.desktopSupported || !deferredInstall) return 'unavailable'
  const prompt = deferredInstall
  await prompt.prompt()
  const choice = await prompt.userChoice
  deferredInstall = null
  updateState({ installAvailable: false, installed: choice.outcome === 'accepted' || isStandalone() })
  return choice.outcome
}

export const applyPwaUpdate = () => {
  if (!registration?.waiting) return false
  reloadForUpdate = true
  registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  return true
}
